import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type StoreRaw = {
  id?: number | string;
  nombre_fantasia?: string;
  nombreFantasia?: string;
  categoria?: string;
};

type AccountItem = {
  id: number;
  provider?: string;
  display_name?: string;
  displayName?: string;
  ml_user_id?: number | null;
  mlUserId?: number | null;
};

type StoresResponse = {
  ok?: boolean;
  stores?: StoreRaw[];
  data?: StoreRaw[];
  result?: StoreRaw[];
  error?: string;
};

type AccountsResponse = {
  ok?: boolean;
  accounts?: AccountItem[];
  data?: AccountItem[];
  result?: AccountItem[];
  error?: string;
};

type GenericResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
  deliveryID?: number;
  deliveryId?: number;
  id?: number;
};

type FormState = {
  storeId: string;
  accountId: string;
  nombreDestinatario: string;
  direccion: string;
  codigoPostal: string;
  localidad: string;
  provincia: string;
  tipoEnvio: string;
  isTurbo: boolean;
};

const initialForm: FormState = {
  storeId: "",
  accountId: "",
  nombreDestinatario: "",
  direccion: "",
  codigoPostal: "",
  localidad: "",
  provincia: "",
  tipoEnvio: "flex",
  isTurbo: false,
};

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const subtitleStyle: React.CSSProperties = {
  margin: "8px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "15px",
  lineHeight: 1.6,
  maxWidth: "900px",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "16px",
};

const singleColumnStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "16px",
};

const twoGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "14px",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionTextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const selectWrapperStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const selectLabelStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 600,
  color: "var(--text-primary)",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "48px",
  padding: "0 16px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  outline: "none",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  fontSize: "15px",
};

const infoBoxStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  padding: "16px",
};

const infoLabelStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  marginBottom: "8px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const infoValueStyle: React.CSSProperties = {
  fontSize: "16px",
  fontWeight: 700,
  color: "var(--text-primary)",
  wordBreak: "break-word",
};

const switchRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  minHeight: "54px",
  padding: "0 16px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
};

const helperStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const actionsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const alertStyle = (tone: "info" | "success" | "danger"): React.CSSProperties => {
  const tones = {
    info: {
      background: "rgba(198,165,106,0.08)",
      border: "1px solid var(--border)",
      color: "var(--text-primary)",
    },
    success: {
      background: "rgba(107,162,125,0.14)",
      border: "1px solid rgba(107,162,125,0.24)",
      color: "var(--success)",
    },
    danger: {
      background: "rgba(185,100,95,0.14)",
      border: "1px solid rgba(185,100,95,0.24)",
      color: "var(--danger)",
    },
  };

  return {
    borderRadius: "16px",
    padding: "14px 16px",
    fontSize: "14px",
    ...tones[tone],
  };
};

function SelectField({
  label,
  value,
  onChange,
  options,
  disabled,
  placeholder = "Seleccionar",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <label style={selectWrapperStyle}>
      <span style={selectLabelStyle}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          ...selectStyle,
          opacity: disabled ? 0.65 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function pickArray<T>(payload: { [key: string]: T[] | undefined }, keys: string[]): T[] {
  for (const key of keys) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function storeName(x: StoreRaw) {
  return x.nombre_fantasia || x.nombreFantasia || `Store ${x.id}`;
}

function accountLabel(x: AccountItem) {
  const name = x.display_name || x.displayName || `Account ${x.id}`;
  const provider = x.provider || "—";
  return `${name} · ${provider}`;
}

function getCreatedDeliveryId(json: GenericResponse): number | null {
  const value = json.deliveryID ?? json.deliveryId ?? json.id;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export default function DeliveriesLoad() {
  const { token } = useAuth();

  const [stores, setStores] = useState<StoreRaw[]>([]);
  const [storeScopedAccounts, setStoreScopedAccounts] = useState<AccountItem[]>([]);

  const [form, setForm] = useState<FormState>(initialForm);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [helperMessage, setHelperMessage] = useState<string>("");

  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      setError("");

      try {
        const storesRes = await authFetch("/v2/stores", { token });
        const storesJson: StoresResponse = await storesRes.json().catch(() => ({}));

        if (!storesRes.ok || storesJson?.ok === false) {
          throw new Error(storesJson?.error || "No se pudieron cargar los stores");
        }

        setStores(pickArray<StoreRaw>(storesJson as never, ["stores", "data", "result"]));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Error cargando datos base");
      } finally {
        setLoading(false);
      }
    };

    loadInitial();
  }, [token]);

  useEffect(() => {
    const loadStoreAccounts = async () => {
      if (!form.storeId) {
        setStoreScopedAccounts([]);
        setHelperMessage("");
        setForm((prev) => ({ ...prev, accountId: "" }));
        return;
      }

      setLoadingAccounts(true);
      setHelperMessage("");
      setStoreScopedAccounts([]);
      setForm((prev) => ({ ...prev, accountId: "" }));

      try {
        const accountsRes = await authFetch(`/v2/stores/${form.storeId}/account`, { token });
        const accountsJson: AccountsResponse = await accountsRes.json().catch(() => ({}));

        if (!accountsRes.ok || accountsJson?.ok === false) {
          throw new Error(accountsJson?.error || "No se pudieron leer las accounts del store");
        }

        const scopedAccounts = pickArray<AccountItem>(accountsJson as never, [
          "accounts",
          "data",
          "result",
        ]);

        setStoreScopedAccounts(scopedAccounts);

        if (scopedAccounts.length === 1) {
          setForm((prev) => ({ ...prev, accountId: String(scopedAccounts[0].id) }));
          setHelperMessage("El store tiene una sola account asociada. Se seleccionó automáticamente.");
        } else if (scopedAccounts.length > 1) {
          setHelperMessage("El store tiene más de una account asociada. Seleccioná el canal correspondiente.");
        } else {
          setHelperMessage("Primero debés crear una account para este store.");
        }
      } catch {
        setStoreScopedAccounts([]);
        setForm((prev) => ({ ...prev, accountId: "" }));
        setHelperMessage("No se pudieron leer las accounts asociadas a este store.");
      } finally {
        setLoadingAccounts(false);
      }
    };

    loadStoreAccounts();
  }, [form.storeId, token]);

  const selectedStore = useMemo(
    () => stores.find((store) => String(store.id) === form.storeId) || null,
    [stores, form.storeId]
  );

  const selectedAccount = useMemo(
    () => storeScopedAccounts.find((account) => String(account.id) === form.accountId) || null,
    [storeScopedAccounts, form.accountId]
  );

  const storeOptions = stores
    .filter((store) => store.id != null)
    .map((store) => ({
      value: String(store.id),
      label: `${storeName(store)} · ${store.categoria || "Sin categoría"}`,
    }));

  const accountOptions = storeScopedAccounts
    .filter((account) => account.id != null)
    .map((account) => ({
      value: String(account.id),
      label: accountLabel(account),
    }));

  const shouldShowAccountSelect =
    Boolean(form.storeId) && storeScopedAccounts.length > 0;

  const shouldDisableAccountSelect =
    loadingAccounts || storeScopedAccounts.length === 1;

  const canSubmit =
    Boolean(form.storeId) &&
    Boolean(form.accountId) &&
    Boolean(form.nombreDestinatario.trim()) &&
    Boolean(form.direccion.trim()) &&
    Boolean(form.codigoPostal.trim()) &&
    Boolean(form.localidad.trim()) &&
    Boolean(form.provincia.trim()) &&
    Boolean(form.tipoEnvio.trim());

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setStoreScopedAccounts([]);
    setSuccess("");
    setError("");
    setHelperMessage("");
  };

  const attachStoreAndAccount = async (deliveryId: number) => {
    const [storeResponse, accountResponse] = await Promise.all([
      authFetch(`/v2/deliveries/${deliveryId}/store`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({ storeId: Number(form.storeId) }),
      }),
      authFetch(`/v2/deliveries/${deliveryId}/account`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({ accountId: Number(form.accountId) }),
      }),
    ]);

    const storeJson: GenericResponse = await storeResponse.json().catch(() => ({}));
    const accountJson: GenericResponse = await accountResponse.json().catch(() => ({}));

    if (!storeResponse.ok || storeJson?.ok === false) {
      throw new Error(storeJson?.error || "El envío se creó, pero no se pudo asociar el store");
    }

    if (!accountResponse.ok || accountJson?.ok === false) {
      throw new Error(accountJson?.error || "El envío se creó, pero no se pudo asociar la account");
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSuccess("");
    setError("");

    const payload = {
      store_id: Number(form.storeId),
      account_id: Number(form.accountId),
      nombre_fantasia: selectedStore ? storeName(selectedStore) : null,
      razon_social: selectedStore ? storeName(selectedStore) : null,
      nombre_destinatario: form.nombreDestinatario.trim(),
      direccion: form.direccion.trim(),
      codigo_postal: form.codigoPostal.trim(),
      localidad: form.localidad.trim(),
      provincia: form.provincia.trim(),
      tipo_envio: form.tipoEnvio.trim(),
      is_turbo: form.isTurbo,
    };

    try {
      setSubmitting(true);

      const response = await authFetch("/v2/deliveries", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: GenericResponse = await response.json().catch(() => ({}));

      if (!response.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo crear el envío");
      }

      const deliveryId = getCreatedDeliveryId(json);

      if (deliveryId) {
        await attachStoreAndAccount(deliveryId);
      } else {
        setHelperMessage("El envío fue creado, pero la API no devolvió ID para asociar store/account.");
      }

      setSuccess("Envío creado correctamente.");
      setForm((prev) => ({
        ...prev,
        nombreDestinatario: "",
        direccion: "",
        codigoPostal: "",
        localidad: "",
        provincia: "",
        tipoEnvio: "flex",
        isTurbo: false,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear el envío");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={pageWrapStyle}>


      {error ? <div style={alertStyle("danger")}>{error}</div> : null}
      {success ? <div style={alertStyle("success")}>{success}</div> : null}
      {helperMessage ? <div style={alertStyle("info")}>{helperMessage}</div> : null}

      <Card>
        <div style={{ marginBottom: "18px" }}>
          <h2 style={sectionTitleStyle}>Selecciona una Tienda</h2>

        </div>

        {loading ? (
          <div style={alertStyle("info")}>Cargando stores...</div>
        ) : (
          <div style={twoGridStyle}>
            <SelectField
              label="Store"
              value={form.storeId}
              onChange={(value) => handleChange("storeId", value)}
              options={storeOptions}
            />

            {shouldShowAccountSelect ? (
              <SelectField
                label="Account"
                value={form.accountId}
                onChange={(value) => handleChange("accountId", value)}
                options={accountOptions}
                disabled={shouldDisableAccountSelect}
                placeholder={
                  loadingAccounts
                    ? "Cargando accounts..."
                    : storeScopedAccounts.length === 1
                      ? "Account seleccionada automáticamente"
                      : "Seleccionar account"
                }
              />
            ) : null}
          </div>
        )}

        <div style={{ height: 18 }} />

        <div style={twoGridStyle}>
          <div style={infoBoxStyle}>
            <div style={infoLabelStyle}>Store seleccionado</div>
            <div style={infoValueStyle}>{selectedStore ? storeName(selectedStore) : "—"}</div>
          </div>

          <div style={infoBoxStyle}>
            <div style={infoLabelStyle}>Account activa</div>
            <div style={infoValueStyle}>
              {selectedAccount ? accountLabel(selectedAccount) : "—"}
            </div>
          </div>
        </div>
      </Card>

      <form onSubmit={handleSubmit}>
        <Card>
          <div style={{ marginBottom: "18px" }}>
            <h2 style={sectionTitleStyle}>Datos del destinatario</h2>
            <p style={sectionTextStyle}>
              Esta versión crea el envío manual y luego asocia store/account usando los endpoints actuales del backend.
            </p>
          </div>

          <div style={gridStyle}>
            <Input
              label="Nombre destinatario"
              placeholder="Ej. Juan Perez"
              value={form.nombreDestinatario}
              onChange={(e) => handleChange("nombreDestinatario", e.target.value)}
            />

            <SelectField
              label="Tipo de envío"
              value={form.tipoEnvio}
              onChange={(value) => handleChange("tipoEnvio", value)}
              options={[
                { value: "flex", label: "Flex" },
                { value: "manual", label: "Manual" },
                { value: "turbo", label: "Turbo" },
              ]}
            />
          </div>

          <div style={{ height: 16 }} />

          <div style={singleColumnStyle}>
            <Input
              label="Dirección"
              placeholder="Ej. Av Rivadavia 1234"
              value={form.direccion}
              onChange={(e) => handleChange("direccion", e.target.value)}
            />
          </div>

          <div style={{ height: 16 }} />

          <div style={gridStyle}>
            <Input
              label="Código postal"
              placeholder="Ej. 1406"
              value={form.codigoPostal}
              onChange={(e) => handleChange("codigoPostal", e.target.value)}
            />

            <Input
              label="Localidad"
              placeholder="Ej. CABA / Morón / Rosario"
              value={form.localidad}
              onChange={(e) => handleChange("localidad", e.target.value)}
            />
          </div>

          <div style={{ height: 16 }} />

          <div style={gridStyle}>
            <Input
              label="Provincia"
              placeholder="Ej. Buenos Aires"
              value={form.provincia}
              onChange={(e) => handleChange("provincia", e.target.value)}
            />

            <div style={selectWrapperStyle}>
              <span style={selectLabelStyle}>Turbo</span>
              <div style={switchRowStyle}>
                <div>
                  <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                    Envío turbo
                  </div>
                  <div style={helperStyle}>
                    Activa <code>is_turbo</code> en el envío.
                  </div>
                </div>

                <input
                  type="checkbox"
                  checked={form.isTurbo}
                  onChange={(e) => handleChange("isTurbo", e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
              </div>
            </div>
          </div>

          <div style={{ height: 24 }} />

          <div style={actionsRowStyle}>
            <Button type="submit" variant="primary" disabled={!canSubmit || submitting}>
              {submitting ? "Creando..." : "Crear envío"}
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={resetForm}
              disabled={submitting}
            >
              Limpiar
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}